import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Upload, File, Lock, Inbox } from 'lucide-react';
import { toast } from 'sonner';
import { submitParserPortalRequest } from '@/services/api/parser';

interface ParserPortalRequestModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ParserPortalRequestModal({ open, onOpenChange }: ParserPortalRequestModalProps) {
  const [form, setForm] = useState({
    portalName: '',
    email: '',
    additionalDetails: '',
  });
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onFileSelected = (f?: File | null) => {
    if (!f) return;
    if (f.type !== 'application/pdf') {
      toast.error('Please upload a PDF file');
      return;
    }
    setFile(f);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) onFileSelected(f);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.portalName.trim() || !form.email.trim() || !file) {
      toast.error('Please fill all required fields and attach a PDF');
      return;
    }

    setSubmitting(true);
    try {
      // Extract authUserId from session storage when available
      let authUserId: string | number | null = null;
      try {
        const raw = sessionStorage.getItem('userData');
        if (raw) {
          const parsed = JSON.parse(raw);
          authUserId = parsed?.authUserId || parsed?.authuserId || parsed?.AuthUserId || parsed?.id || null;
        }
      } catch {}

      await submitParserPortalRequest({
        supplierName: form.portalName.trim(),
        email: form.email.trim(),
        sampleBill: file,
        additionalInfo: form.additionalDetails.trim() || undefined,
        authUserId: authUserId ?? undefined,
      });
      toast.success('Request submitted', {
        description: 'We will review your portal request and get back to you soon.',
      });
      // reset and close
      setForm({ portalName: '', email: '', additionalDetails: '' });
      setFile(null);
      onOpenChange(false);
    } catch (err: any) {
      toast.error('Submission failed', {
        description: err?.response?.data?.message || err?.message || 'Unable to submit request',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Inbox className="w-5 h-5 text-blue-600" />
            Request New Parser
          </DialogTitle>
          <DialogDescription>
            Provide a sample bill and contact details.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="portalName">Portal Name *</Label>
              <Input
                id="portalName"
                value={form.portalName}
                onChange={(e) => setForm((p) => ({ ...p, portalName: e.target.value }))}
                placeholder="e.g., PG&E, ConEd, Duke Energy"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Contact Email *</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                placeholder="your.email@company.com"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Upload Sample Bill (PDF) *</Label>
            {file ? (
              <div className="flex items-center justify-between gap-2 px-3 py-2 border rounded-md bg-green-50 border-green-300">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <File className="h-4 w-4 text-green-600 flex-shrink-0" />
                  <span className="text-sm font-medium truncate">{file.name}</span>
                  <span className="text-xs text-slate-500 flex-shrink-0">{(file.size / 1024).toFixed(0)} KB</span>
                </div>
                <Button type="button" variant="ghost" size="sm" className="h-6 px-2" onClick={() => setFile(null)}>
                  Remove
                </Button>
              </div>
            ) : (
              <label
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={onDrop}
                className={
                  `flex flex-col items-center justify-center gap-2 px-3 py-8 border-2 border-dashed rounded-md cursor-pointer transition-colors ${
                    isDragging ? 'border-blue-400 bg-blue-50/50' : 'border-slate-300 hover:border-blue-400'
                  }`
                }
              >
                <Upload className="h-6 w-6 text-slate-400" />
                <span className="text-sm text-slate-700">Drag and drop PDF here, or click to upload</span>
                <Input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => onFileSelected(e.target.files?.[0] || null)}
                  className="hidden"
                />
              </label>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="details">Additional Details (optional)</Label>
            <Textarea
              id="details"
              rows={3}
              placeholder="Any specific fields to extract or additional information..."
              value={form.additionalDetails}
              onChange={(e) => setForm((p) => ({ ...p, additionalDetails: e.target.value }))}
            />
          </div>

          <Alert>
            <Lock className="h-4 w-4" />
            <AlertDescription>
              Sample bill will be used only for parser testing and validation.
            </AlertDescription>
          </Alert>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit Request'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
